import React from 'react';
import styled from 'styled-components';
import { Card, Button, Tag } from 'antd';
import { CheckCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { PROCUREMENT_TIERS, getTierInfo, formatTierFee } from '../utils/tierClassification';

const TierContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TierCard = styled(Card)`
  .ant-card-body {
    padding: 16px;
  }
  
  border: 2px solid ${props => 
    props.selected ? '#1890ff' : '#f0f0f0'
  };
  
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #1890ff;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
  }
  
  ${props => props.selected && `
    background: rgba(24, 144, 255, 0.05);
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
  `}
`;

const TierHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TierLabel = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #262626;
`;

const TierFee = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: #1890ff;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TierDescription = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 12px;
`;

const CategoryTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const CategoryTag = styled(Tag)`
  margin: 0;
  font-size: 11px;
  border-radius: 8px;
  padding: 2px 6px;
`;

const SelectorHeader = styled.div`
  margin-bottom: 16px;
  text-align: center;
`;

const SelectorTitle = styled.h4`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
`;

const SelectorSubtitle = styled.div`
  font-size: 13px;
  color: #8c8c8c;
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  color: #1890ff;
`;

/**
 * Tier Selector Component
 * Allows users to select their search-fee tier before dispatch
 */
function TierSelector({ selectedTier, onTierSelect, autoSelected = false }) {
  const selectedTierInfo = getTierInfo(selectedTier);
  
  const getSampleCategories = (categories) => {
    return categories.slice(0, 3).map(cat => 
      cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    );
  };

  return (
    <div>
      <SelectorHeader>
        <SelectorTitle>Select Your Project Type</SelectorTitle>
        <SelectorSubtitle>
          {autoSelected 
            ? `Auto-detected: ${selectedTierInfo.label} Tier (${formatTierFee(selectedTier)})`
            : 'Choose the tier that best matches your project'
          }
        </SelectorSubtitle>
      </SelectorHeader>
      
      <TierContainer>
        {Object.entries(PROCUREMENT_TIERS).map(([tierKey, tier]) => (
          <TierCard
            key={tierKey}
            selected={selectedTier === tierKey}
            onClick={() => onTierSelect(tierKey)}
            hoverable={false}
          >
            {selectedTier === tierKey && (
              <SelectedIndicator>
                <CheckCircleOutlined />
              </SelectedIndicator>
            )}
            
            <TierHeader>
              <TierLabel>{tier.label}</TierLabel>
              <TierFee>
                <DollarOutlined />
                {tier.fee}
              </TierFee>
            </TierHeader>
            
            <TierDescription>
              {tier.description}
            </TierDescription>
            
            <CategoryTags>
              {getSampleCategories(tier.categories).map(category => (
                <CategoryTag key={category} color="blue">
                  {category}
                </CategoryTag>
              ))}
              {tier.categories.length > 3 && (
                <CategoryTag color="default">
                  +{tier.categories.length - 3} more
                </CategoryTag>
              )}
            </CategoryTags>
          </TierCard>
        ))}
      </TierContainer>
    </div>
  );
}

export default TierSelector;